import React, { FC, ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { shadeSafe, tintSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { Fill } from '../../../ui/Fill';
import { Emoji, EmojiData, EmojiStore } from '../../../ui/emoji';
import { Token } from '../../packages/tokens/Token';
import { RenderBounds, RenderBox, RenderEmoticon, RenderInner, RenderUrl, RenderUserInfo, useBracketShortcut, usePose } from './RenderHelpers';
import { RenderText } from './RenderText';

const usePoseDerivatives = (object: Token) => {
    const p = usePose(object);
    return useMemo(() => ({
        highlighted: !!(p.pressed || p.targeted || p.selected) && !p.beingManipulated,
        selected: p.selected,
        editing: p.beingEdited,
    }), [p]);
};

const useEmoji = (value: string) => {
    return useMemo(() => EmojiStore.find(value), [value]);
};

const RenderDesign = React.memo(({ color, selected, highlighted }: { color: string, selected: boolean, highlighted: boolean }) => {

    const fill = tintSafe(.75, color);
    const border = tintSafe(highlighted ? (selected ? 0 : .2) : .4, color);
    const shade = shadeSafe(0.075, border);

    return <>
        <RenderBox inset="0" background={shade} borderRadius="50%" transform="translate(0, 3px)" />
        <RenderBox inset="0" background={fill} borderRadius="50%" border={`solid 8px ${border}`} />
    </>;
});

export const TokenVisual: FC<{ object: Token, children?: ReactNode }> = ({ object, children }) => {

    const { highlighted, selected, editing } = usePoseDerivatives(object);
    const emoji = useEmoji(object.value);
    const bs = useBracketShortcut(object.value);

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderDesign {...{ color: object.background, highlighted, selected }} />
                {!editing && <>
                    {!!emoji && <RenderEmoji emoji={emoji} />}
                    {!emoji && <>
                        {bs.type === 'none' && <RenderText {...object.layout.value} value={object.value} formatOpts={[]} />}
                        {bs.type === 'emoticon' && <RenderEmoticon value={bs.value} size={Math.min(object.width, object.height) * .7} color={object.layout.value.settings.fontColor} />}
                        {bs.type === 'user' && <RenderUserInfo value={bs.value} inset="8px" borderRadius="50%" overflow="hidden" />}
                        {bs.type === 'url' && <RenderUrl value={bs.value} inset="8px" borderRadius="50%" overflow="hidden" />}
                    </>}
                </>}
            </RenderInner>
        </RenderBounds>
        {children}
    </>;
};

const EmojiFrame = styled(Fill)`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 40px;
`;

const RenderEmoji = React.memo(({ emoji }: { emoji: EmojiData }) => {
    return <EmojiFrame><Emoji idOrCode={emoji.id} /></EmojiFrame>;
});
